import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_SHORT,
  HERO_IMAGE_SOURCE_URL,
  HERO_IMAGE_URL,
  MAP_URL,
  ADVANCEMENT_FOCUS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Support', href: '#support' },
    { label: 'Advancement', href: '#advancement' },
    { label: 'Location', href: '#location' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-white/10 bg-navy/95 text-white backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex min-w-0 items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded border border-gold/50 bg-oxblood text-xs font-black text-ivory">
            FCCA
          </span>
          <span className="truncate text-sm font-bold tracking-wide text-ivory sm:text-base">{COMPANY_SHORT}</span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-semibold text-ivory/75 md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-gold">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#location"
          className="rounded border border-gold/60 px-4 py-2 text-sm font-bold text-gold transition hover:bg-gold hover:text-navy"
        >
          View Address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="border-b border-line bg-ivory">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 lg:grid-cols-[0.92fr_1.08fr] lg:items-stretch lg:py-20">
        <aside className="relative min-h-[420px] overflow-hidden rounded border border-line bg-navy text-ivory shadow-soft">
          <img
            src={HERO_IMAGE_URL}
            alt="Cincinnati Classical Academy page examples"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/55 via-navy/25 to-navy/90" />
          <div className="relative flex min-h-[420px] flex-col justify-between p-7">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.24em] text-gold">Classical Support Profile</p>
              <a
                href={HERO_IMAGE_SOURCE_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex rounded bg-white/10 px-3 py-2 text-xs font-bold text-ivory backdrop-blur transition hover:bg-gold hover:text-navy"
              >
                Public image source
              </a>
            </div>
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-ivory/70">Address</p>
              <p className="mt-3 text-xl font-black leading-snug">{COMPANY_ADDRESS_FULL}</p>
            </div>
          </div>
        </aside>

        <div className="flex flex-col justify-center border-y border-line py-8 lg:border-y-0 lg:border-l lg:pl-10">
          <p className="text-sm font-black uppercase tracking-[0.22em] text-oxblood">
            Support for classical education in Cincinnati
          </p>
          <h1 className="mt-5 max-w-4xl text-4xl font-black leading-tight tracking-normal text-navy sm:text-5xl lg:text-6xl">
            {COMPANY_NAME}
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-8 text-muted">
            A restrained public profile for the supporting organization connected to Cincinnati Classical Academy and
            its development, advancement, and school-community mission.
          </p>
          <p className="mt-4 max-w-3xl leading-7 text-muted">
            The design follows classical-school cues: formal navy, oxblood, warm gold, ivory surfaces, and an editorial
            structure inspired by the academy's time-tested tradition.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="#support"
              className="rounded bg-oxblood px-5 py-3 text-center font-bold text-white shadow-sm transition hover:bg-navy"
            >
              View Support Role
            </a>
            <a
              href="#advancement"
              className="rounded border border-gold bg-gold px-5 py-3 text-center font-bold text-navy shadow-sm transition hover:bg-vellum"
            >
              Advancement Focus
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactsStrip() {
  return (
    <section aria-label="Profile facts" className="bg-vellum">
      <div className="mx-auto grid max-w-7xl divide-y divide-line px-5 md:grid-cols-3 md:divide-x md:divide-y-0">
        {PROFILE_FACTS.map((fact) => (
          <div key={fact.label} className="py-6 md:px-6 first:md:pl-0 last:md:pr-0">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-oxblood">{fact.label}</p>
            <p className="mt-2 text-xl font-black text-navy">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, children }) {
  return (
    <div className="max-w-2xl">
      <p className="text-sm font-black uppercase tracking-[0.18em] text-oxblood">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-black text-navy sm:text-4xl">{title}</h2>
      {children ? <p className="mt-4 leading-7 text-muted">{children}</p> : null}
    </div>
  );
}

function SupportSection() {
  return (
    <section id="support" className="bg-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-[0.65fr_1.35fr] lg:py-20">
        <SectionHeading eyebrow="Support Role" title="A formal profile for school advancement">
          Public sources describe Friends as the supporting organization for Cincinnati Classical Academy's development
          and advancement.
        </SectionHeading>
        <div className="grid gap-5 md:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded border border-line bg-ivory p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-oxblood">Organization</p>
            <p className="mt-3 text-2xl font-black leading-snug text-navy">{COMPANY_NAME}</p>
          </div>
          <div className="rounded border border-line bg-vellum p-6">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-oxblood">Profile Note</p>
            <p className="mt-3 leading-8 text-muted">
              This page keeps the source-backed organization address visible while avoiding unsupported operational,
              staff, or donation-system details.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AdvancementSection() {
  return (
    <section id="advancement" className="bg-ivory">
      <div className="mx-auto max-w-7xl px-5 py-16 lg:py-20">
        <div className="flex flex-col gap-6 border-b border-line pb-10 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Advancement" title="Classical-school context, conservative data">
            The layout borrows from prep-school editorial systems: strong institutional color, measured spacing, and
            numbered rows instead of generic marketing cards.
          </SectionHeading>
          <p className="max-w-sm text-sm font-bold uppercase tracking-[0.18em] text-muted">
            Time-tested tradition, civic virtue, and school-community support.
          </p>
        </div>
        <div className="mt-10 divide-y divide-line border-y border-line">
          {ADVANCEMENT_FOCUS.map((note) => (
            <article key={note.title} className="grid gap-4 py-7 md:grid-cols-[0.16fr_0.34fr_0.5fr] md:items-start">
              <p className="font-black text-gold">{note.number}</p>
              <h3 className="text-xl font-black text-navy">{note.title}</h3>
              <p className="leading-7 text-muted">{note.body}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {PUBLIC_INFO.map((item) => (
            <div key={item.label} className="rounded border border-line bg-white p-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-oxblood">{item.label}</p>
              <p className="mt-2 text-xl font-black text-navy">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocationBand() {
  return (
    <section id="location" className="bg-navy text-white">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-16 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
        <div>
          <p className="text-sm font-black uppercase tracking-[0.18em] text-gold">Location</p>
          <h2 className="mt-3 text-3xl font-black sm:text-4xl">{COMPANY_LOCATION}</h2>
          <p className="mt-4 max-w-xl leading-7 text-white/70">
            Use the supplied address below for public profile, directory, and correspondence reference.
          </p>
        </div>
        <div className="rounded border border-gold/25 bg-white/8 p-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-white/55">Organization Address</p>
          <p className="mt-3 text-2xl font-black">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex rounded bg-gold px-5 py-3 font-bold text-navy transition hover:bg-white"
          >
            Open map
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-oxblood text-white/75">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-white">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-ivory text-ink">
      <a
        href="#support"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-oxblood focus:px-3 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsStrip />
        <SupportSection />
        <AdvancementSection />
        <LocationBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
